/**
 * PATH: packages/kanban-host/src/allocation/AllocateToKanbanSubMenu.tsx
 * FILE_ID: KANBANHOST-ALLOCATETOKANBANSUBMENU-001
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/allocation/AllocateToKanbanSubMenu.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/allocation/AllocateToKanbanSubMenu.tsx: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  FolderTree,
  Home,
  Kanban,
  Pin,
  PinOff,
  Plus,
  Sparkles,
  Star,
} from 'lucide-react'

import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
} from '../_internal/components/ContextMenu'

import type { KanbanApi, Lane } from '@quotech/ui/kanban'
import { getLaneColor } from '@quotech/ui/kanban'

import {
  isPinnedBoard,
  togglePinnedBoard,
} from '../index'
import type { BoardTreeNode } from '../index'
import {
  useAllocateToKanbanMenuData,
  type AllocateMenuBoard,
} from '../index'
import { useKanbanBoardLanes } from '../index'
import {
  useKanbanQuickAllocate,
  type QuickAllocateOutcome,
} from '../index'

/**
 * Module A follow-up — when the email being right-clicked has an
 * active AI_SUGGESTED_PENDING allocation, surface its confidence
 * next to the lane the AI chose so operators can decide whether to
 * trust the suggestion before clicking another lane (or before
 * accepting via the ✓ in the row chip).
 *
 * `confidence` is the raw `Allocation.confidence` from the BE
 * (range 0.0–1.0). We render `Math.round(value * 100)` so the pill
 * shows "AI 73%" — same scale + same threshold (<0.6 = amber) as
 * the kanban card pill, which operators are already trained on.
 */
export interface AllocateAiSuggestion {
  /** Board the AI chose. The board row matching this id stays
   *  expanded by default so the operator doesn't have to hunt for
   *  it in a long list. */
  boardId: string
  /** Lane the AI chose within `boardId`. The lane row matching this
   *  id renders the confidence pill. */
  laneId: string
  /** LLM confidence at allocation time, 0.0–1.0. When null the pill
   *  is hidden (the BE allocation existed but no LLM ran — e.g.
   *  legacy data). */
  confidence: number | null
}

/**
 * Pure I → O — scope an `AllocateAiSuggestion` to a single board.
 *
 * Returns the suggestion unchanged when it targets this board, and
 * `null` otherwise. Exported so the BoardRow can keep its render
 * declarative AND so the unit tests can pin the scoping rule
 * without having to mount the whole context-menu tree.
 *
 *   in  → ({boardId: 'a', laneId: 'x', confidence: 0.7}, 'a')
 *   out → ({boardId: 'a', laneId: 'x', confidence: 0.7})
 *
 *   in  → ({boardId: 'a', laneId: 'x', confidence: 0.7}, 'b')
 *   out → null
 *
 *   in  → (null, 'a')
 *   out → null
 */
export function scopeAiSuggestionToBoard(
  suggestion: AllocateAiSuggestion | null | undefined,
  boardId: string,
): AllocateAiSuggestion | null {
  if (!suggestion) return null
  if (suggestion.boardId !== boardId) return null
  return suggestion
}

/**
 * EK-14 — pure helper that decides whether to send the
 * `supersedesAllocationId` field on a submenu allocate call.
 *
 * Rules (every branch unit-tested in
 * `AllocateToKanbanSubMenu.supersedes.test.tsx`):
 *
 * 1. No `priorAllocationId` → no supersede (nothing to override).
 * 2. Operator picked the AI's EXACT lane → no supersede (tick-equiv).
 *    The chip's accept path handles that case; routing through
 *    submenu-as-supersede would write a confusing "AI was wrong …
 *    but the operator picked the same lane" correction row.
 * 3. Anything else (different lane on same board, different board,
 *    no AI suggestion but a prior `priorAllocationId` exists because
 *    the prior was RULE / AI_AUTO without a separate suggestion
 *    hint) → return the prior id so the BE writes the correction.
 *    (The BE's `should_record_supersedes_correction` gate still
 *    filters to AI-only signals — RULE / USER_MANUAL priors are
 *    revoked without a corpus row.)
 */
export function pickSupersedesId(args: {
  priorAllocationId: string | null
  aiSuggestion: AllocateAiSuggestion | null
  chosenBoardId: string
  chosenLaneId: string
}): string | undefined {
  if (!args.priorAllocationId) return undefined
  if (
    args.aiSuggestion &&
    args.aiSuggestion.boardId === args.chosenBoardId &&
    args.aiSuggestion.laneId === args.chosenLaneId
  ) {
    return undefined
  }
  return args.priorAllocationId
}

export interface AllocateToKanbanSubMenuProps {
  api: KanbanApi
  orgId: number | null
  emailId: string
  /** When more than one id is set, one lane choice allocates every id. */
  emailIds?: string[]
  /** Called after a successful allocate (kind === 'ok' OR 'conflict').
   *  The parent typically closes the surrounding ContextMenu and
   *  refetches its email list. */
  onAllocated?: (outcome: QuickAllocateOutcome) => void
  /** Tenant toast bridge. */
  notify?: (kind: 'success' | 'error' | 'info', message: string) => void
  /** Module A follow-up — the email's current AI suggestion (if any).
   *  Pass the email's `allocation_summary` mapped to `{boardId,
   *  laneId, confidence}` ONLY when `source === 'AI_SUGGESTED_PENDING'`.
   *  Omit (or pass `null`) for emails with no pending AI suggestion;
   *  the submenu then renders no confidence pill anywhere. */
  aiSuggestion?: AllocateAiSuggestion | null
  /**
   * EK-14 — id of the existing AI / rule-originated allocation on this
   * email. When present, picking ANY board other than the AI's suggestion
   * passes this through to `useKanbanQuickAllocate` so the BE writes a
   * `ClassificationCorrection` row capturing the override (board + lane).
   * Pass `null` when there's no prior allocation OR when the prior was
   * `USER_MANUAL` (no AI signal to learn from).
   *
   * Wire from `email.allocation_summary` when
   * `source ∈ {AI_AUTO, AI_SUGGESTED_PENDING, AI_SUGGESTED_USER_CONFIRMED}`.
   */
  priorAllocationId?: string | null
  /**
   * Optional: master Quotech user id. When provided, the Pin / Unpin
   * row writes the operator's pinned-board ids under the per-(user,
   * org) v2 key (`kanban:pinned-boards-v2:<userId>:<orgId>`) so two
   * operators sharing a browser don't see each other's pins.
   * Postmortem 2026-05-19 follow-up #6 (qf!3196). When omitted,
   * pins continue using the v1 per-org key (legacy contract). */
  currentUserId?: number | string | null
  /**
   * FEAT-2026-06-03-ALLOCATE-MENU-CREATE — when provided, the submenu
   * renders a "+ New board…" footer row (and surfaces it in the empty
   * state). Clicking it calls this callback; the HOST owns the actual
   * board-creation surface because it must OUTLIVE this submenu — the
   * submenu unmounts the moment the context menu closes, so a dialog
   * mounted here would be torn down before the operator could type.
   * The host (EmailContextMenuProvider) opens a <CreateBoardDialog>
   * and, on success, allocates the email into the new board's default
   * lane. Omit to hide the row entirely (e.g. read-only mounts). */
  onRequestCreateBoard?: () => void
}

export function AllocateToKanbanSubMenu({
  api,
  orgId,
  emailId,
  emailIds,
  onAllocated,
  notify,
  aiSuggestion,
  priorAllocationId,
  currentUserId = null,
  onRequestCreateBoard,
}: AllocateToKanbanSubMenuProps) {
  const menu = useAllocateToKanbanMenuData({ api, orgId, currentUserId })
  const allocate = useKanbanQuickAllocate({
    api,
    orgId,
    notify,
    currentUserId,
  })

  const doAllocate = async (
    board: AllocateMenuBoard,
    laneId: string,
  ) => {
    const ids = emailIds && emailIds.length > 0 ? emailIds : [emailId]
    const outcome = ids.length > 1
      ? await allocate.runMany({
          emailIds: ids,
          laneId,
          boardId: board.id,
          source: 'USER_MANUAL',
        })
      : await allocate.run({
      emailId: ids[0],
      laneId,
      boardId: board.id,
      source: 'USER_MANUAL',
      // EK-14 — only flag as a "supersedes" call when the operator
      // is picking a different lane / board than the AI suggested.
      // Picking the AI's exact lane via the submenu is a tick-equivalent
      // and routes through the chip's accept path, not here.
      supersedesAllocationId: pickSupersedesId({
        priorAllocationId: priorAllocationId ?? null,
        aiSuggestion: aiSuggestion ?? null,
        chosenBoardId: board.id,
        chosenLaneId: laneId,
      }),
    })
    onAllocated?.(outcome)
  }

  const renderLoading = menu.isLoading && menu.totalCount === 0
  const renderEmpty = !menu.isLoading && menu.totalCount === 0

  return (
    <ContextMenuSub
      label={
        emailIds && emailIds.length > 1
          ? `Allocate ${emailIds.length} to Kanban`
          : 'Allocate to Kanban'
      }
      icon={Kanban}
    >
      {/* EK-14 — when the email has a prior AI-originated allocation,
          surface a hint at the top of the submenu so operators know
          their override is a learning signal. Pure visual; the actual
          supersede wire happens in `doAllocate` via
          `pickSupersedesId`. */}
      {priorAllocationId && aiSuggestion && (
        <div
          className="px-3 py-1.5 text-[10px] border-b border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100"
          aria-label="AI suggestion learning hint"
        >
          <div className="flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            <span className="font-medium">AI suggested this email's home.</span>
          </div>
          <div className="text-[9px] text-amber-800/80 dark:text-amber-200/70 mt-0.5">
            Picking a different lane teaches the AI for next time.
          </div>
        </div>
      )}
      {renderLoading && (
        <div className="px-3 py-2 text-[11px] text-muted-foreground">
          Loading boards…
        </div>
      )}
      {menu.isError && (
        <div className="px-3 py-2 text-[11px] text-red-500">
          Could not load boards.
        </div>
      )}
      {renderEmpty && (
        <div className="px-3 py-2 text-[11px] text-muted-foreground">
          {onRequestCreateBoard
            ? 'No boards yet — create your first one below.'
            : 'No boards available — create one in Kanban first.'}
        </div>
      )}

      {/* FEAT-2026-05-24-KANBAN-BOARD-HOME — top-of-list "Home"
          bucket, when the operator has set one AND it's still
          writable. Rendered as a SINGLE row with a Home icon so it
          visually stands apart from Pinned. Click expands into the
          board's lane list the same way every other board row does;
          there is no "auto-drop into primary lane" shortcut here
          because skipping the lane picker would surprise the
          operator the first time they used the menu after upgrading.
          The lane picker still defaults to whatever the operator
          last touched on this board via the recents bump. */}
      {menu.home && (
        <>
          <BucketLabel>
            <span className="flex items-center gap-1">
              <Home className="h-3 w-3" />
              Home
            </span>
          </BucketLabel>
          <BoardRow
            key={menu.home.id}
            board={menu.home}
            orgId={orgId}
            api={api}
            busy={allocate.busy}
            onAllocateBoard={doAllocate}
            subBoardsByParentId={menu.childrenByParentId}
            aiSuggestion={aiSuggestion}
            currentUserId={currentUserId}
          />
        </>
      )}

      {menu.pinned.length > 0 && (
        <>
          {menu.home && <ContextMenuSeparator />}
          <BucketLabel>Pinned</BucketLabel>
          {menu.pinned.map((board) => (
            <BoardRow
              key={board.id}
              board={board}
              orgId={orgId}
              api={api}
              busy={allocate.busy}
              onAllocateBoard={doAllocate}
              subBoardsByParentId={menu.childrenByParentId}
              aiSuggestion={aiSuggestion}
              currentUserId={currentUserId}
            />
          ))}
        </>
      )}

      {menu.recent.length > 0 && (
        <>
          {(menu.home || menu.pinned.length > 0) && <ContextMenuSeparator />}
          <BucketLabel>Recent</BucketLabel>
          {menu.recent.map((board) => (
            <BoardRow
              key={board.id}
              board={board}
              orgId={orgId}
              api={api}
              busy={allocate.busy}
              onAllocateBoard={doAllocate}
              subBoardsByParentId={menu.childrenByParentId}
              aiSuggestion={aiSuggestion}
              currentUserId={currentUserId}
            />
          ))}
        </>
      )}

      {menu.all.length > 0 && (
        <>
          {(menu.home || menu.pinned.length > 0 || menu.recent.length > 0) && (
            <ContextMenuSeparator />
          )}
          <BucketLabel>All boards</BucketLabel>
          <BoardTreeNodeView
            node={menu.tree}
            orgId={orgId}
            api={api}
            busy={allocate.busy}
            onAllocate={doAllocate}
            subBoardsByParentId={menu.childrenByParentId}
            aiSuggestion={aiSuggestion}
            currentUserId={currentUserId}
          />
        </>
      )}

      {/* FEAT-2026-06-03-ALLOCATE-MENU-CREATE — "+ New board…" footer.
          The operator complained that the only escape hatch when the
          board they wanted didn't exist was the empty-state copy
          ("create one in Kanban first") — a context switch out of the
          inbox. This row keeps them in flow: clicking it hands off to
          the host, which opens the full CreateBoardDialog (kind /
          template / lanes) and allocates THIS email into the new
          board on success. */}
      {onRequestCreateBoard && (
        <>
          {menu.totalCount > 0 && <ContextMenuSeparator />}
          <ContextMenuItem icon={Plus} onClick={onRequestCreateBoard}>
            New board…
          </ContextMenuItem>
        </>
      )}
    </ContextMenuSub>
  )
}

// ──────────────────────────────────────────────────────────
// Tree renderer — recursive ContextMenuSub-based
// ──────────────────────────────────────────────────────────

interface BoardTreeNodeViewProps {
  /** The tree node to render. The ROOT node's `children` and
   *  `boards` are rendered inline (no wrapping submenu); inner
   *  nodes render as a `<ContextMenuSub>` with their label + leaf
   *  count and recurse on click-into. */
  node: BoardTreeNode
  orgId: number | null
  api: KanbanApi
  busy: boolean
  onAllocate: (board: AllocateMenuBoard, laneId: string) => void | Promise<void>
  /** FEAT-2026-06-03-KANBAN-JOB-SUBBOARD-ROUTING — forwarded to each
   *  leaf BoardRow so a top-level board in the "All boards" tree also
   *  nests its real sub-boards. */
  subBoardsByParentId?: Map<string, AllocateMenuBoard[]>
  aiSuggestion?: AllocateAiSuggestion | null
  currentUserId?: number | string | null
}

/**
 * Recursive renderer for the tree.
 *
 *   - At ROOT (node.label === ''): render children + boards inline
 *     (no wrapping submenu — the parent already opened "Allocate to
 *     Kanban").
 *   - At any GROUP node: render a `<ContextMenuSub label="2026 (8)">`
 *     and recurse so the hover-to-open chain mirrors the data tree.
 *   - LEAF boards: render `<BoardRow>` exactly as the flat path did
 *     so allocation + lane sub-list behaviour is unchanged.
 *
 * Why groups render BEFORE root.boards (when both are present): the
 * tree is the meaningful structure the operator opted into; their
 * un-renamed legacy boards (root.boards) are the tail. Operators
 * trying to find "this year's WLG site measure" should hit groups
 * first.
 */
function BoardTreeNodeView({
  node,
  orgId,
  api,
  busy,
  onAllocate,
  subBoardsByParentId,
  aiSuggestion,
  currentUserId = null,
}: BoardTreeNodeViewProps) {
  const isRoot = node.label === ''

  const body = (
    <>
      {node.children.map((child) => (
        <ContextMenuSub
          key={child.path}
          label={`${child.label} (${child.leafCount})`}
          icon={FolderTree}
        >
          <BoardTreeNodeView
            node={child}
            orgId={orgId}
            api={api}
            busy={busy}
            onAllocate={onAllocate}
            subBoardsByParentId={subBoardsByParentId}
            aiSuggestion={aiSuggestion}
            currentUserId={currentUserId}
          />
        </ContextMenuSub>
      ))}
      {node.boards.map((board) => (
        <BoardRow
          key={board.id}
          board={board}
          orgId={orgId}
          api={api}
          busy={busy}
          onAllocateBoard={onAllocate}
          subBoardsByParentId={subBoardsByParentId}
          aiSuggestion={aiSuggestion}
          currentUserId={currentUserId}
        />
      ))}
    </>
  )

  // Root inlines into its parent ContextMenuSub; group nodes wrap
  // their own submenu — that wrapping happens in the caller above
  // via `<ContextMenuSub label="…">`, so this function only ever
  // returns the BODY.
  return isRoot ? body : body
}

// ──────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────

function BucketLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
      {children}
    </div>
  )
}

interface BoardRowProps {
  board: AllocateMenuBoard
  orgId: number | null
  api: KanbanApi
  busy: boolean
  /** Board-level allocate. Receives the board explicitly so a nested
   *  sub-board row can target ITS OWN board, not its parent's. The
   *  row binds its own lane handler internally. */
  onAllocateBoard: (
    board: AllocateMenuBoard,
    laneId: string,
  ) => void | Promise<void>
  /** FEAT-2026-06-03-KANBAN-JOB-SUBBOARD-ROUTING — real
   *  `Board.parent_board` children keyed by parent id. When this
   *  board has entries, they render nested under its lane list as
   *  their own BoardRows (mirroring the Kanban left rail). Threaded
   *  recursively so the lookup is uniform, though the backend caps
   *  depth at 2 so a sub-board never itself has children. */
  subBoardsByParentId?: Map<string, AllocateMenuBoard[]>
  /** Module A follow-up — forwarded to LaneList so the matching
   *  lane renders its confidence pill. The lane only renders the
   *  pill when BOTH this row's board id AND the suggestion's lane id
   *  match, which prevents the pill from leaking into unrelated
   *  boards that happen to share a lane name. */
  aiSuggestion?: AllocateAiSuggestion | null
  currentUserId?: number | string | null
}

function BoardRow({
  board,
  orgId,
  api,
  busy,
  onAllocateBoard,
  subBoardsByParentId,
  aiSuggestion,
  currentUserId = null,
}: BoardRowProps) {
  // `hovered` flips the first time the submenu OPENS (driven by
  // ContextMenuSub's own hover detection). We don't wrap with our
  // own `<div onMouseEnter>` — that approach (used pre-qf!2894)
  // had two wrappers competing for the same mouseenter event and
  // the outer one wouldn't reliably fire in production, leaving
  // `useKanbanBoardLanes` with `enabled=false` forever and the
  // operator staring at "Loading lanes…" — see
  // BUG-2026-05-13-PINNED-BOARDROW-NO-LANES.
  const [hovered, setHovered] = useState(false)
  // Lazy-fetch lanes the FIRST time the submenu opens. react-query
  // caches by ['kanban-lanes', boardId] so a second open is free.
  const lanes = useKanbanBoardLanes({
    api,
    boardId: board.id,
    enabled: hovered,
  })

  const defaultLaneId = lanes.defaultLaneId ?? null
  const canQuickAllocate = Boolean(defaultLaneId) && !busy
  // Only forward the suggestion when it targets THIS board. Keeps
  // the lane list inside an unrelated board free of any pill.
  // Delegated to a pure helper so the scoping rule is unit-testable.
  const scopedSuggestion = scopeAiSuggestionToBoard(aiSuggestion, board.id)
  // Bind the board-level allocate to THIS row's board.
  const handleLane = (laneId: string) => {
    void onAllocateBoard(board, laneId)
  }
  const subBoards = subBoardsByParentId?.get(board.id) ?? []

  // FEAT-2026-06-03 — append the new lane after the current tail so
  // it doesn't displace existing lane positions. Lanes are loaded by
  // the time this panel is open (hover triggers the fetch); empty
  // list falls back to position 10.
  const nextLanePosition =
    lanes.lanes
      .filter((l) => !l.archived)
      .reduce((max, l) => Math.max(max, l.position), 0) + 10

  return (
    <ContextMenuSub
      label={board.name}
      dot={board.swatch ?? undefined}
      disabled={busy}
      onClick={
        canQuickAllocate && defaultLaneId
          ? () => handleLane(defaultLaneId)
          : undefined
      }
      onOpenChange={(open) => {
        if (open) setHovered(true)
      }}
    >
      <LaneList
        lanes={lanes.lanes}
        isLoading={lanes.isLoading}
        isError={lanes.isError}
        defaultLaneId={defaultLaneId}
        busy={busy}
        onSelect={handleLane}
        aiSuggestion={scopedSuggestion}
      />
      {subBoards.length > 0 && (
        <>
          <ContextMenuSeparator />
          <BucketLabel>
            <span className="flex items-center gap-1">
              <FolderTree className="h-3 w-3" />
              Sub-boards
            </span>
          </BucketLabel>
          {subBoards.map((sub) => (
            <BoardRow
              key={sub.id}
              board={sub}
              orgId={orgId}
              api={api}
              busy={busy}
              onAllocateBoard={onAllocateBoard}
              subBoardsByParentId={subBoardsByParentId}
              aiSuggestion={aiSuggestion}
              currentUserId={currentUserId}
            />
          ))}
        </>
      )}
      {/* FEAT-2026-06-03-ALLOCATE-MENU-CREATE — inline "+ New lane".
          createLane only needs board + name, so (unlike board
          creation) we can do it in-place without a modal. On success
          we allocate THIS email straight into the freshly-created
          lane. Hidden while the lane list is still loading so the
          operator doesn't create a lane against a half-known board. */}
      {!lanes.isLoading && (
        <NewLaneInlineRow
          api={api}
          boardId={board.id}
          nextPosition={nextLanePosition}
          busy={busy}
          onCreated={handleLane}
        />
      )}
      <ContextMenuSeparator />
      <PinToggleRow
        boardId={board.id}
        orgId={orgId}
        currentUserId={currentUserId}
      />
    </ContextMenuSub>
  )
}

/**
 * FEAT-2026-06-03-ALLOCATE-MENU-CREATE — inline lane creator rendered
 * at the bottom of a board's lane list.
 *
 * Why inline (vs. a modal like board creation): a lane only needs a
 * name (`createLane({ board, name, kind })`), so a single text field
 * is the whole form. Keeping it inside the submenu panel means the
 * operator never leaves the allocate flow — type a name, hit Enter,
 * and the email lands in the new lane.
 *
 * It is safe to render an <input> inside the context-menu panel: the
 * panel is tagged `data-quotech-cmenu` and stops click propagation,
 * so neither focusing the field nor typing dismisses the menu (the
 * root only closes on clicks/scrolls OUTSIDE that scope). We still
 * stopPropagation on the wrapper defensively.
 */
export function NewLaneInlineRow({
  api,
  boardId,
  nextPosition,
  busy,
  onCreated,
}: {
  api: KanbanApi
  boardId: string
  nextPosition: number
  busy: boolean
  onCreated: (laneId: string) => void
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setSaving(true)
    setError(null)
    try {
      // Default kind CUSTOM — mirrors the in-board AddLaneButton's
      // default. The 30+ model lane kinds are mostly internal
      // state-machine routing and shouldn't be exposed for ad-hoc
      // creation; CUSTOM is the neutral "just a list" kind.
      const lane = await api.createLane({
        board: boardId,
        name: trimmed,
        kind: 'CUSTOM',
        position: nextPosition,
      })
      // Refresh the per-board lane cache so the row's own list (and
      // any other open surface) shows the new lane immediately.
      await queryClient.invalidateQueries({
        queryKey: ['kanban-lanes', boardId],
      })
      onCreated(lane.id)
    } catch (err) {
      setError((err as Error)?.message || 'Could not create lane')
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <ContextMenuItem
        icon={Plus}
        disabled={busy}
        onClick={() => setEditing(true)}
      >
        New lane…
      </ContextMenuItem>
    )
  }

  return (
    <div
      className="px-2 py-1.5"
      data-quotech-cmenu="panel"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void submit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
            setName('')
            setError(null)
          }
        }}
        placeholder="New lane name…"
        disabled={saving}
        className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
      />
      {error && (
        <div className="mt-1 text-[10px] text-red-500">{error}</div>
      )}
      <div className="mt-1 flex items-center gap-1">
        <button
          type="button"
          disabled={saving || !name.trim()}
          onClick={() => void submit()}
          className="rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? 'Adding…' : 'Add lane'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            setEditing(false)
            setName('')
            setError(null)
          }}
          className="rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export interface LaneListProps {
  lanes: Lane[]
  isLoading: boolean
  isError: boolean
  defaultLaneId: string | null
  busy: boolean
  onSelect: (laneId: string) => void
  /** When present, the lane whose id matches `aiSuggestion.laneId`
   *  renders an "AI 73%" pill next to its name. Null = no AI
   *  suggestion for this board. */
  aiSuggestion?: AllocateAiSuggestion | null
}

export function LaneList({
  lanes,
  isLoading,
  isError,
  defaultLaneId,
  busy,
  onSelect,
  aiSuggestion,
}: LaneListProps) {
  const sorted = useMemo(() => {
    return lanes
      .filter((l) => !l.archived)
      .slice()
      .sort((a, b) => a.position - b.position)
  }, [lanes])

  if (isLoading && sorted.length === 0) {
    return <div className="px-3 py-1.5 text-[11px] text-muted-foreground">Loading lanes…</div>
  }
  if (isError) {
    return <div className="px-3 py-1.5 text-[11px] text-red-500">Could not load lanes.</div>
  }
  if (sorted.length === 0) {
    return (
      <div className="px-3 py-1.5 text-[11px] text-muted-foreground">
        No lanes — add one in Kanban first.
      </div>
    )
  }

  return (
    <>
      {sorted.map((lane) => {
        const isDefault = lane.id === defaultLaneId
        const swatch = getLaneColor(lane)
        const isAiSuggested =
          aiSuggestion != null && aiSuggestion.laneId === lane.id
        return (
          <ContextMenuItem
            key={lane.id}
            dot={swatch}
            disabled={busy}
            onClick={() => onSelect(lane.id)}
          >
            <span className="flex items-center gap-1.5">
              <span className="truncate">{lane.name}</span>
              {typeof lane.card_count === 'number' && (
                <span className="text-[9px] text-muted-foreground/70">
                  · {lane.card_count}
                </span>
              )}
              {isDefault && (
                <Star
                  className="h-2.5 w-2.5 text-amber-500 shrink-0"
                  aria-label="Default lane"
                />
              )}
              {isAiSuggested && (
                <AiConfidencePill confidence={aiSuggestion!.confidence} />
              )}
            </span>
          </ContextMenuItem>
        )
      })}
    </>
  )
}

/**
 * AI confidence chip — mirrors the KanbanCard pill at
 * `packages/ui/src/kanban/components/KanbanCard.tsx:491-510` so
 * operators see the same shape + colour rules in both surfaces.
 *
 * Render rule (matches the card pill):
 *   confidence == null  → no pill (no LLM ran / legacy allocation)
 *   confidence >= 0.6   → grey pill   "AI 87%"
 *   confidence <  0.6   → amber pill  "AI 41%" (operator must verify)
 */
function AiConfidencePill({ confidence }: { confidence: number | null }) {
  if (confidence == null) return null
  const pct = Math.round(confidence * 100)
  const low = confidence < 0.6
  return (
    <span
      className={[
        'shrink-0 ml-auto inline-flex items-center gap-0.5 rounded-full',
        'px-1.5 py-0.5 text-[9.5px] font-medium leading-none',
        low
          ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/60'
          : 'bg-neutral-100 text-neutral-600 dark:bg-zinc-800/80 dark:text-zinc-400',
      ].join(' ')}
      title={
        low
          ? `Low-confidence AI suggestion (${pct}%) — verify before confirming`
          : `AI suggested this lane with ${pct}% confidence`
      }
      aria-label={`AI confidence ${pct} percent`}
    >
      <Sparkles className="w-2.5 h-2.5" aria-hidden="true" />
      AI {pct}%
    </span>
  )
}

function PinToggleRow({
  boardId,
  orgId,
  currentUserId = null,
}: {
  boardId: string
  orgId: number | null
  currentUserId?: number | string | null
}) {
  const [pinned, setPinned] = useState<boolean>(() =>
    isPinnedBoard(orgId, boardId, currentUserId),
  )

  const handlePin = () => {
    const next = togglePinnedBoard(orgId, boardId, currentUserId)
    setPinned(next.includes(boardId))
  }

  return (
    <ContextMenuItem
      icon={pinned ? PinOff : Pin}
      onClick={handlePin}
    >
      {pinned ? 'Unpin board' : 'Pin board'}
    </ContextMenuItem>
  )
}
